import type { CollectionName } from './data-port.ts';
import type { Scope } from './query.ts';
import type { Entity } from './repository.ts';

/**
 * What the caller believes the document currently says.
 *
 * `updatedAt` is the version: every stored entity carries one, so this needs
 * no extra field and no counter to keep in step. A write carrying an
 * expectation applies only if the document still matches it.
 *
 * This is what makes read-compute-write safe when several people are working
 * at once. A schedule cascade reads the whole schedule, works out what has to
 * move, and writes it back — without a guard, two people dragging bars at the
 * same moment both compute from the same snapshot and the second commit
 * silently discards the first person's cascade. Nothing errors; the dates are
 * just wrong, and nobody knows which write won.
 */
export interface Expectation {
  /** The `updatedAt` value the caller last read for this document. */
  readonly updatedAt: string;
}

/**
 * One write inside an atomic batch.
 *
 * Carries its own scope and collection so a batch can span both — a schedule
 * cascade touches one collection, but releasing an invoice touches several,
 * and splitting that across two commits would leave the books disagreeing
 * with themselves.
 */
export type BatchWrite =
  | {
      readonly kind: 'create';
      readonly scope: Scope;
      readonly collection: CollectionName;
      readonly entity: Entity;
    }
  | {
      readonly kind: 'update';
      readonly scope: Scope;
      readonly collection: CollectionName;
      readonly id: string;
      readonly patch: Readonly<Record<string, unknown>>;
      readonly expect?: Expectation;
    }
  | {
      readonly kind: 'delete';
      readonly scope: Scope;
      readonly collection: CollectionName;
      readonly id: string;
      readonly expect?: Expectation;
    };

/**
 * Firestore's limit on writes in one batch, applied everywhere so a batch
 * that works locally cannot fail only in production.
 *
 * Splitting a larger batch would silently give up the all-or-nothing
 * guarantee that is the whole point, so it is refused instead.
 */
export const MAX_BATCH_WRITES = 500;

export class BatchTooLargeError extends Error {
  readonly size: number;

  constructor(size: number) {
    super(
      `A batch may hold at most ${MAX_BATCH_WRITES} writes; this one has ${size}. ` +
        'Splitting it would give up the all-or-nothing guarantee, so it is refused.',
    );
    this.name = 'BatchTooLargeError';
    this.size = size;
  }
}

/** Typed constructors, so a caller cannot put the wrong shape in a batch. */
export function createWrite<T extends Entity>(
  scope: Scope,
  collection: CollectionName,
  entity: T,
): BatchWrite {
  return { kind: 'create', scope, collection, entity };
}

export function updateWrite<T extends Entity>(
  scope: Scope,
  collection: CollectionName,
  id: string,
  patch: Partial<Omit<T, 'id'>>,
  expect?: Expectation,
): BatchWrite {
  return {
    kind: 'update',
    scope,
    collection,
    id,
    patch: patch as Record<string, unknown>,
    expect,
  };
}

export function deleteWrite(
  scope: Scope,
  collection: CollectionName,
  id: string,
  expect?: Expectation,
): BatchWrite {
  return { kind: 'delete', scope, collection, id, expect };
}

/** Reads the expectation off a document the caller has in hand. */
export function expectUnchanged(entity: { readonly updatedAt: string }): Expectation {
  return { updatedAt: entity.updatedAt };
}
